import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../components/ui/TopNavbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import KPICard from './components/KPICard';
import FilterPanel from './components/FilterPanel';
import AlertPanel from './components/AlertPanel';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import SystemStatus from './components/SystemStatus';

const OperationsDashboard = () => {
  const navigate = useNavigate();
  const [currentRole, setCurrentRole] = useState('operations');
  const [filters, setFilters] = useState({
    dateRange: 'month',
    supplier: 'all',
    buyer: 'all',
    port: 'all',
    vessel: 'all',
    status: 'all'
  });
  const [savedViews, setSavedViews] = useState([
  { name: 'Monthly Overview', filters: { dateRange: 'month', supplier: 'all', buyer: 'all', port: 'all', vessel: 'all', status: 'all' } },
  { name: 'Critical Issues', filters: { dateRange: 'week', supplier: 'all', buyer: 'all', port: 'all', vessel: 'all', status: 'delayed' } }]
  );

  const kpiData = {
    operations: [
    {
      title: 'Active Shipments',
      value: '8',
      subtitle: '3-4 suppliers per month',
      icon: 'Ship',
      trend: 'up',
      trendValue: '+12%',
      color: 'primary',
      onClick: () => navigate('/shipment-register')
    },
    {
      title: 'Monthly Tonnage',
      value: '1,050,000 MT',
      subtitle: 'Target: 1,000,000 MT',
      icon: 'Package',
      trend: 'up',
      trendValue: '+5%',
      color: 'success',
      onClick: () => navigate('/shipment-register')
    },
    {
      title: 'Pending Costs',
      value: '$3,412,500',
      subtitle: '18 entries awaiting approval',
      icon: 'DollarSign',
      trend: 'neutral',
      trendValue: '0%',
      color: 'warning',
      onClick: () => navigate('/cost-register')
    },
    {
      title: 'Overdue Issues',
      value: '7',
      subtitle: '3 critical, 4 high priority',
      icon: 'AlertTriangle',
      trend: 'down',
      trendValue: '-2',
      color: 'error',
      onClick: () => navigate('/issue-and-risk-log')
    },
    {
      title: 'Fee Reconciliation',
      value: '$3,493,750',
      subtitle: 'December pending (~1M MT)',
      icon: 'Receipt',
      trend: 'up',
      trendValue: '+15%',
      color: 'accent',
      onClick: () => navigate('/fee-register')
    },
    {
      title: 'Documents Pending',
      value: '12',
      subtitle: 'B/L and invoices',
      icon: 'FileText',
      trend: 'neutral',
      trendValue: '0',
      color: 'primary',
      onClick: () => navigate('/document-management-center')
    }],

    finance: [
    {
      title: 'Total Costs (Dec)',
      value: '$3,412,500',
      subtitle: 'Across 8 major shipments (~1M MT)',
      icon: 'DollarSign',
      trend: 'up',
      trendValue: '+12%',
      color: 'success',
      onClick: () => navigate('/cost-register')
    },
    {
      title: 'Fee Revenue',
      value: '$3,493,750',
      subtitle: 'Pending reconciliation (~1M MT)',
      icon: 'Receipt',
      trend: 'up',
      trendValue: '+15%',
      color: 'accent',
      onClick: () => navigate('/fee-register')
    },
    {
      title: 'Reimbursable Costs',
      value: '$852,300',
      subtitle: 'Awaiting reimbursement',
      icon: 'RefreshCw',
      trend: 'neutral',
      trendValue: '0%',
      color: 'warning',
      onClick: () => navigate('/cost-register')
    },
    {
      title: 'Outstanding Invoices',
      value: '8',
      subtitle: 'Total: $2,437,500',
      icon: 'FileText',
      trend: 'down',
      trendValue: '-3',
      color: 'primary',
      onClick: () => navigate('/reports-and-analytics')
    }],

    rnr: [
    {
      title: 'Your Shipments',
      value: '6',
      subtitle: '3-4 suppliers active',
      icon: 'Ship',
      trend: 'up',
      trendValue: '+3',
      color: 'primary',
      onClick: () => navigate('/shipment-register')
    },
    {
      title: 'Issues Reported',
      value: '4',
      subtitle: '2 require attention',
      icon: 'AlertTriangle',
      trend: 'neutral',
      trendValue: '0',
      color: 'warning',
      onClick: () => navigate('/issue-and-risk-log')
    },
    {
      title: 'Monthly Volume',
      value: '1,050,000 MT',
      subtitle: 'December 2025 (~1M MT target)',
      icon: 'Package',
      trend: 'up',
      trendValue: '+10%',
      color: 'success',
      onClick: () => navigate('/reports-and-analytics')
    }]

  };

  const alerts = [
  {
    id: 1,
    severity: 'critical',
    title: 'Vessel Delay - MV Pacific Glory',
    message: 'Expected arrival delayed by 48 hours due to weather conditions. 320,000 MT shipment affected - demurrage risk identified.',
    time: '15 mins ago',
    shipment: 'SHP-2025-0142'
  },
  {
    id: 2,
    severity: 'high',
    title: 'B/L Documentation Missing',
    message: 'Bill of Lading not received for 150,000 MT shipment SHP-2025-0138. Payment release blocked.',
    time: '1 hour ago',
    shipment: 'SHP-2025-0138'
  },
  {
    id: 3,
    severity: 'high',
    title: 'Cost Approval Pending',
    message: 'Inspection costs of $124,500 for 380,000 MT shipment awaiting approval for 5 days. Vendor payment overdue.',
    time: '3 hours ago',
    shipment: 'SHP-2025-0135'
  },
  {
    id: 4,
    severity: 'medium',
    title: 'Fee Reconciliation Due',
    message: 'December fee reconciliation pending. Total amount: $3,493,750 across 8 large shipments (1.05M MT total).',
    time: '5 hours ago',
    shipment: null
  },
  {
    id: 5,
    severity: 'medium',
    title: 'LC Expiry Warning',
    message: 'Letter of Credit for Adani Power 185,000 MT shipment expires in 7 days. Extension required.',
    time: '1 day ago',
    shipment: 'SHP-2025-0140'
  }];


  const activities = [
  {
    id: 1,
    type: 'shipment',
    user: 'Sarah Mitchell',
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16e75c406-1763294340369.png",
    userAvatarAlt: 'Professional headshot of Caucasian woman with blonde hair in navy blazer smiling at camera',
    action: 'updated shipment status to In Transit',
    details: 'MV Pacific Glory (320,000 MT) departed Port of Newcastle',
    time: '10 mins ago',
    reference: 'SHP-2025-0143'
  },
  {
    id: 2,
    type: 'cost',
    user: 'James Chen',
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1665ca73c-1763296377705.png",
    userAvatarAlt: 'Professional headshot of Asian man with short black hair in gray suit with confident expression',
    action: 'approved inspection costs',
    details: 'Amount: $87,500 for 380,000 MT coal quality testing',
    time: '25 mins ago',
    reference: 'COST-2025-0892'
  },
  {
    id: 3,
    type: 'issue',
    user: 'System Alert',
    userAvatar: null,
    userAvatarAlt: null,
    action: 'created new issue',
    details: 'Quality specification mismatch detected on 265,000 MT shipment',
    time: '1 hour ago',
    reference: 'ISS-2025-0067'
  },
  {
    id: 4,
    type: 'document',
    user: 'Emily Rodriguez',
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_119848aeb-1763295351165.png",
    userAvatarAlt: 'Professional headshot of Hispanic woman with long brown hair in white blouse with warm smile',
    action: 'uploaded Bill of Lading',
    details: 'Document verified and approved for 150,000 MT shipment',
    time: '2 hours ago',
    reference: 'SHP-2025-0141'
  },
  {
    id: 5,
    type: 'user',
    user: 'Michael Kumar',
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19d0e6f0d-1763295389190.png",
    userAvatarAlt: 'Professional headshot of Indian man with short black hair in dark blue suit with professional demeanor',
    action: 'completed monthly review',
    details: 'December 2025 operational summary - 1.05M MT total volume from 3-4 suppliers',
    time: '3 hours ago',
    reference: 'REV-2025-12'
  },
  {
    id: 6,
    type: 'cost',
    user: 'Lisa Thompson',
    userAvatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17faa8e7a-1763295754680.png",
    userAvatarAlt: 'Professional headshot of African American woman with curly hair in burgundy blazer with confident smile',
    action: 'submitted reimbursement request',
    details: 'Travel expenses: $32,400 for supplier site visits',
    time: '4 hours ago',
    reference: 'COST-2025-0895'
  }];


  const quickActions = [
  {
    id: 1,
    label: 'Add New Shipment',
    description: 'Register new coal shipment',
    icon: 'Plus',
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    action: () => navigate('/shipment-register')
  },
  {
    id: 2,
    label: 'Record Cost Entry',
    description: 'Add inspection or logistics cost',
    icon: 'DollarSign',
    color: 'text-success',
    bgColor: 'bg-success/10',
    action: () => navigate('/cost-register')
  },
  {
    id: 3,
    label: 'Log New Issue',
    description: 'Report operational issue',
    icon: 'AlertTriangle',
    color: 'text-error',
    bgColor: 'bg-error/10',
    action: () => navigate('/issue-and-risk-log')
  },
  {
    id: 4,
    label: 'Generate Report',
    description: 'Export operational data',
    icon: 'FileText',
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    action: () => navigate('/reports-and-analytics')
  },
  {
    id: 5,
    label: 'Upload Documents',
    description: 'Add B/L or invoices',
    icon: 'Upload',
    color: 'text-secondary',
    bgColor: 'bg-secondary/10',
    action: () => navigate('/document-management-center')
  }];


  const systemStatus = [
  {
    id: 1,
    name: 'Banking Integration',
    description: 'Payment tracking',
    status: 'connected',
    lastSync: '5 mins ago'
  },
  {
    id: 2,
    name: 'Shipping Portal',
    description: 'Vessel tracking',
    status: 'warning',
    lastSync: '15 mins ago'
  },
  {
    id: 3,
    name: 'Inspection System',
    description: 'Lab results feed',
    status: 'connected',
    lastSync: '1 min ago'
  }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: 'month',
      supplier: 'all',
      buyer: 'all',
      port: 'all',
      vessel: 'all',
      status: 'all'
    });
  };

  const handleSaveView = (name, filterData) => {
    setSavedViews((prev) => [...prev, { name, filters: filterData }]);
  };

  const handleLoadView = (view) => {
    setFilters(view?.filters);
  };

  const handleAlertClick = (alert) => {
    if (alert?.shipment) {
      navigate(`/shipment-register?id=${alert?.shipment}`);
    }
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismiss alert:', alertId);
  };

  const handleActionClick = (action) => {
    action?.action();
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.key >= '1' && e?.key <= '6') {
        const index = parseInt(e?.key) - 1;
        const currentKPIs = kpiData?.[currentRole];
        if (currentKPIs?.[index] && currentKPIs?.[index]?.onClick) {
          currentKPIs?.[index]?.onClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentRole]);

  return (
    <div className="min-h-screen bg-background">
      <TopNavbar />
      <div className="main-content-with-topnav">
        <div className="sticky top-[65px] z-40 bg-card border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Operations Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Real-time visibility into coal trading operations
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="role-badge">
                  <Icon name="Shield" size={14} />
                  <span>{currentRole === 'operations' ? 'RVA Operations' : currentRole === 'finance' ? 'RVA Finance' : 'RNR Staff'}</span>
                </div>
                <Button variant="outline" size="sm" iconName="Bell">
                  <span className="ml-2">5</span>
                </Button>
                <Button variant="default" size="sm" iconName="Download">
                  Export
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={currentRole === 'operations' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentRole('operations')}>
                Operations View
              </Button>
              <Button
                variant={currentRole === 'finance' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentRole('finance')}>
                Finance View
              </Button>
              <Button
                variant={currentRole === 'rnr' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCurrentRole('rnr')}>
                RNR View
              </Button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {kpiData?.[currentRole]?.map((kpi, index) =>
            <KPICard key={index} {...kpi} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
                savedViews={savedViews}
                onSaveView={handleSaveView}
                onLoadView={handleLoadView} />

              <SystemStatus systems={systemStatus} />
            </div>

            <div className="lg:col-span-6 space-y-6">
              <AlertPanel
                alerts={alerts}
                onAlertClick={handleAlertClick}
                onDismiss={handleDismissAlert} />
            </div>

            <div className="lg:col-span-3 space-y-6">
              <QuickActions actions={quickActions} onActionClick={handleActionClick} />
              <ActivityFeed activities={activities} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperationsDashboard;